import * as React from 'react';
import { Download, ExternalLink } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

export interface Invoice {
  id: string;
  number: string | null;
  amount: number;
  currency: string;
  status: 'draft' | 'open' | 'paid' | 'uncollectible' | 'void';
  created: string;
  periodStart: string;
  periodEnd: string;
  invoicePdf: string | null;
  hostedInvoiceUrl: string | null;
}

interface InvoiceListProps {
  invoices: Invoice[];
  isLoading?: boolean;
}

export function InvoiceList({ invoices, isLoading }: InvoiceListProps): React.JSX.Element {
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatAmount = (amount: number, currency: string): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const getStatusBadge = (status: Invoice['status']): React.JSX.Element => {
    switch (status) {
      case 'paid':
        return <Badge variant="default">Paid</Badge>;
      case 'open':
        return <Badge variant="warning">Open</Badge>;
      case 'draft':
        return <Badge variant="secondary">Draft</Badge>;
      case 'void':
        return <Badge variant="outline">Void</Badge>;
      case 'uncollectible':
        return <Badge variant="destructive">Uncollectible</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const handleDownloadPdf = async (invoiceId: string): Promise<void> => {
    try {
      window.open(`/api/v1/billing/invoices/${invoiceId}/pdf`, '_blank');
    } catch (error) {
      console.error('Failed to download invoice PDF:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading invoices...</div>
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-muted-foreground mb-2">No invoices yet</p>
        <p className="text-sm text-muted-foreground">
          Your invoices will appear here once you have an active subscription
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Invoice</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Period</TableHead>
            <TableHead>Amount</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoices.map((invoice) => (
            <TableRow key={invoice.id}>
              <TableCell className="font-medium">
                {invoice.number || invoice.id.substring(0, 8)}
              </TableCell>
              <TableCell>{formatDate(invoice.created)}</TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatDate(invoice.periodStart)} - {formatDate(invoice.periodEnd)}
              </TableCell>
              <TableCell>{formatAmount(invoice.amount, invoice.currency)}</TableCell>
              <TableCell>{getStatusBadge(invoice.status)}</TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                  {invoice.invoicePdf && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownloadPdf(invoice.id)}
                      title="Download PDF"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  )}
                  {invoice.hostedInvoiceUrl && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(invoice.hostedInvoiceUrl!, '_blank')}
                      title="View in Stripe"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
